import { Injectable } from '@angular/core';
import { throwOnGraphqlError } from '@app/core';
import { ApolloQueryResult } from '@apollo/client';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  WebhookEventsGQL,
  CreateWebhookEndpointGQL,
  CreateWebhookEndpointMutationVariables,
  DeleteWebhookEndpointGQL,
  DeleteWebhookEndpointMutationVariables,
  FailedWebhooksGQL,
  FailedWebhooksQuery,
  ResendFailedWebhookGQL,
  ResendFailedWebhookMutationVariables,
  UnblockWebhookEndpointGQL,
  UnblockWebhookEndpointMutationVariables,
  UpdateWebhookEndpointGQL,
  UpdateWebhookEndpointMutationVariables,
  WebhookEndpoint,
  WebhookEndpointsGQL,
  DeleteLegacyEndpointGQL
} from 'src/generated/graphql.default';

@Injectable({
  providedIn: 'root'
})
export class WebhooksService {
  private webhooksListSubject = new BehaviorSubject<WebhookEndpoint[]>([]);
  webhooksList$ = this.webhooksListSubject.asObservable();

  public isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private webhookEventsGQL: WebhookEventsGQL,
    private webhookEndpointsGQL: WebhookEndpointsGQL,
    private failedWebhooksGQL: FailedWebhooksGQL,
    private createEndpointGQL: CreateWebhookEndpointGQL,
    private deleteEndpointGQL: DeleteWebhookEndpointGQL,
    private resendFailedWebhooktGQL: ResendFailedWebhookGQL,
    private unblockEndpointGQL: UnblockWebhookEndpointGQL,
    private updateEndpointGQL: UpdateWebhookEndpointGQL,
    private deleteLegacyEndpointGQL: DeleteLegacyEndpointGQL
  ) {}

  getAutentiqueEvents() {
    return this.webhookEventsGQL.fetch(null).pipe(
      throwOnGraphqlError(),
      map(response => response.data.webhookEvents)
    );
  }

  getOrganizationWebhooksEndpoints() {
    this.isLoadingSubject.next(true);

    return this.webhookEndpointsGQL.fetch(null).pipe(
      throwOnGraphqlError(),
      tap(response => {
        this.webhooksListSubject.next(response.data.webhookEndpoints);
        this.isLoadingSubject.next(false);
      }),
      map(response => response.data.webhookEndpoints)
    );
  }

  getFailedWebhooks(page: number = 1, limit: number = 60): Observable<ApolloQueryResult<FailedWebhooksQuery>> {
    this.isLoadingSubject.next(true);
    return this.failedWebhooksGQL.fetch({ is_resolved: false, page, limit }).pipe(throwOnGraphqlError());
  }

  createWebhookEndpoint(variables: CreateWebhookEndpointMutationVariables) {
    this.isLoadingSubject.next(true);
    return this.createEndpointGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createEndpoint)
    );
  }

  deleteWebhookEndpoint(variables: DeleteWebhookEndpointMutationVariables) {
    this.isLoadingSubject.next(true);
    return this.deleteEndpointGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      tap(() => {
        this.isLoadingSubject.next(false);
      }),
      map(response => {
        return response.data.deleteEndpoint;
      })
    );
  }

  updateWebhookEndpoint(variables: UpdateWebhookEndpointMutationVariables) {
    this.isLoadingSubject.next(true);
    return this.updateEndpointGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => {
        return response.data.updateEndpoint;
      })
    );
  }

  unblockEndpointMutation(variables: UnblockWebhookEndpointMutationVariables) {
    this.isLoadingSubject.next(true);
    return this.unblockEndpointGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => {
        return response.data.unblockEndpoint;
      })
    );
  }

  resendFailedWebhook(variables: ResendFailedWebhookMutationVariables) {
    return this.resendFailedWebhooktGQL.mutate(variables, { fetchPolicy: 'no-cache' });
  }

  deleteLegacyEndpoint() {
    this.isLoadingSubject.next(true);
    return this.deleteLegacyEndpointGQL.mutate().pipe(
      throwOnGraphqlError(),
      map(response => {
        return response.data.deleteLegacyEndpoint;
      })
    );
  }
}
